import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Logo } from '../Logo';
import { SearchBar } from '../SearchBar';

const designers = [
  { name: 'Van Cleef & Arpels', slug: 'van-cleef-arpels' },
  { name: 'Cartier', slug: 'cartier' },
  { name: 'Tiffany & Co.', slug: 'tiffany-co' },
  { name: 'Bulgari', slug: 'bulgari' },
  { name: 'Harry Winston', slug: 'harry-winston' },
  { name: 'Chopard', slug: 'chopard' },
  { name: 'Graff', slug: 'graff' },
  { name: 'Piaget', slug: 'piaget' },
  { name: 'Boucheron', slug: 'boucheron' },
  { name: 'Mikimoto', slug: 'mikimoto' },
];

const guides = [
  { name: 'Authentication Guide', slug: 'authenticate-luxury-jewelry' },
  { name: 'Condition Grades', slug: 'condition-grades-explained' },
  { name: 'Resale Value Guide', slug: 'resale-value-guide' },
  { name: 'Buying Online', slug: 'buy-preowned-jewelry-online' },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinkClass = (path) => 
    `text-xs tracking-[0.15em] uppercase transition-colors duration-300 ${
      location.pathname === path 
        ? 'text-[#C69C6D]' 
        : 'text-[#555] hover:text-[#111]'
    }`;

  return (
    <header className="sticky top-0 z-50 glass-nav" data-testid="navbar">
      {/* Affiliate disclosure banner */}
      <div className="bg-[#111] text-white/70 text-[10px] tracking-[0.1em] uppercase py-2.5 text-center">
        <span>This site contains affiliate links. </span>
        <Link to="/affiliate-disclosure" className="text-[#C69C6D] hover:text-white transition-colors">
          Learn more
        </Link>
      </div>
      
      <nav className="container-editorial">
        <div className="flex items-center justify-between py-5 border-b border-[#E5E0D8]">
          {/* Logo */}
          <Logo variant="dark" size="md" linkTo="/" />

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-10">
            <Link to="/" className={navLinkClass('/')}>
              Home
            </Link>

            {/* Designers Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className={`flex items-center gap-1.5 ${navLinkClass('/designers')}`}>
                Designers
                <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56 bg-white border border-[#E5E0D8] shadow-none rounded-none p-2">
                {designers.map((designer) => (
                  <DropdownMenuItem key={designer.slug} asChild>
                    <Link 
                      to={`/designers/${designer.slug}`}
                      className="px-4 py-2.5 text-sm text-[#555] hover:text-[#111] hover:bg-[#FAF9F6] cursor-pointer transition-colors"
                    >
                      {designer.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Guides Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className={`flex items-center gap-1.5 ${navLinkClass('/guides')}`}>
                Guides
                <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56 bg-white border border-[#E5E0D8] shadow-none rounded-none p-2">
                {guides.map((guide) => (
                  <DropdownMenuItem key={guide.slug} asChild>
                    <Link 
                      to={`/guides/${guide.slug}`}
                      className="px-4 py-2.5 text-sm text-[#555] hover:text-[#111] hover:bg-[#FAF9F6] cursor-pointer transition-colors"
                    >
                      {guide.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/blog" className={navLinkClass('/blog')}>
              Articles
            </Link>

            <Link to="/investments" className={navLinkClass('/investments')} data-testid="nav-investments">
              Index
            </Link>

            <Link to="/drops" className={navLinkClass('/drops')} data-testid="nav-drops">
              Drops
            </Link>

            <Link to="/celebrity-jewelry" className={navLinkClass('/celebrity-jewelry')} data-testid="nav-celebrity">
              Celebrity
            </Link>

            <Link to="/tools" className={navLinkClass('/tools')} data-testid="nav-tools">
              Tools
            </Link>

            <Link to="/about" className={navLinkClass('/about')}>
              About
            </Link>

            <Link to="/contact" className={navLinkClass('/contact')}>
              Contact
            </Link>

            {/* Desktop Search */}
            <SearchBar variant="desktop" />
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <button className="p-2 text-[#111]" data-testid="mobile-menu-button">
                {isOpen ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-96 bg-[#FAF9F6] border-l border-[#E5E0D8] p-0">
              <SheetTitle className="sr-only">Site navigation</SheetTitle>
              <div className="flex flex-col h-full">
                {/* Mobile Header */}
                <div className="p-6 border-b border-[#E5E0D8]">
                  <Logo variant="dark" size="sm" linkTo="/" />
                </div>

                {/* Mobile Search */}
                <SearchBar variant="mobile" onNavigate={() => setIsOpen(false)} />

                {/* Mobile Links */}
                <div className="flex-1 overflow-y-auto py-6">
                  <div className="space-y-1 px-6">
                    <Link 
                      to="/" 
                      onClick={() => setIsOpen(false)}
                      className="block py-3 text-[#111] border-b border-[#E5E0D8]"
                    >
                      Home
                    </Link>
                    <Link 
                      to="/blog" 
                      onClick={() => setIsOpen(false)}
                      className="block py-3 text-[#111] border-b border-[#E5E0D8]"
                    >
                      Articles
                    </Link>
                    <Link 
                      to="/investments" 
                      onClick={() => setIsOpen(false)}
                      className="block py-3 text-[#111] border-b border-[#E5E0D8]"
                    >
                      Investment Index
                    </Link>
                    <Link 
                      to="/drops" 
                      onClick={() => setIsOpen(false)}
                      className="block py-3 text-[#111] border-b border-[#E5E0D8]"
                    >
                      Drop Tracker
                    </Link>
                    <Link 
                      to="/celebrity-jewelry" 
                      onClick={() => setIsOpen(false)}
                      className="block py-3 text-[#111] border-b border-[#E5E0D8]"
                    >
                      Celebrity Jewelry
                    </Link>
                    <Link 
                      to="/tools" 
                      onClick={() => setIsOpen(false)}
                      className="block py-3 text-[#111] border-b border-[#E5E0D8]"
                      data-testid="mobile-nav-tools"
                    >
                      Tools
                    </Link>
                    <Link 
                      to="/about" 
                      onClick={() => setIsOpen(false)}
                      className="block py-3 text-[#111] border-b border-[#E5E0D8]"
                    >
                      About
                    </Link>
                    <Link 
                      to="/contact" 
                      onClick={() => setIsOpen(false)}
                      className="block py-3 text-[#111] border-b border-[#E5E0D8]"
                    >
                      Contact
                    </Link>
                  </div>

                  {/* Designers */}
                  <div className="mt-8 px-6">
                    <p className="overline overline-gold mb-4">Designers</p>
                    <div className="grid grid-cols-2 gap-2">
                      {designers.map((designer) => (
                        <Link
                          key={designer.slug}
                          to={`/designers/${designer.slug}`}
                          onClick={() => setIsOpen(false)}
                          className="text-sm text-[#555] hover:text-[#111] py-2"
                        >
                          {designer.name}
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Guides */}
                  <div className="mt-8 px-6">
                    <p className="overline overline-gold mb-4">Guides</p>
                    <div className="space-y-2">
                      {guides.map((guide) => (
                        <Link
                          key={guide.slug}
                          to={`/guides/${guide.slug}`}
                          onClick={() => setIsOpen(false)}
                          className="block text-sm text-[#555] hover:text-[#111] py-2"
                        >
                          {guide.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
